import { useForm, useFieldArray, useWatch } from "react-hook-form";
import type { UseFormReturn } from "react-hook-form";
import DataTable from "../common/table";
import { Button, FormField, Form, FormMessage, Checkbox } from "../ui";
import { formFromStructure } from "./so-form";
import { useTranslation } from "react-i18next";
import { FormFieldType, getField } from "./fields";
import { useEffect, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import { ComboBox } from "../common/combo-box";
import { exportFormCsv, importFormCsv } from "~/utils/csv-util";
import type { ColumnDef, Table } from "@tanstack/react-table";
import { defaultTableColumns } from "~/utils/table-util";
import {getRerenderFieldNames, isBulkFormData, zodSchemaFromStructure} from "~/utils/form-util";
import z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import type { BulkFormData, FormOption, FormStructure } from "~/types/ui";
import { ContactCreateDialog } from "./custom/contact-select";
import {Switch} from "~/components/ui/switch";
import {Loader2} from "lucide-react";

export function BulkEditableTable<T>({
  structure,
  data,
  onSubmit,
  disableRowAlteration = false,
  hideSearchbar = false,
  overrideSaveButtonLable,
}: {
  structure: FormStructure<T>;
  data: T[];
  onSubmit: (rows: BulkFormData<T>) => void;
  disableRowAlteration?: boolean;
  hideSearchbar?: boolean;
  overrideSaveButtonLable?: string;
}) {
  const { t } = useTranslation();
  const [table, setTable] = useState<Table<any>>();
  const bulkSchema = z.object({
    items: z.array(zodSchemaFromStructure(structure, t)),
  });
  const form = useForm<BulkFormData<any>>({
    defaultValues: { items: data },
    resolver: zodResolver(bulkSchema),
  });
  const { fields, append } = useFieldArray<BulkFormData<any>>({
    control: form.control,
    name: "items",
  });

  const [columns, setColumns] = useState<ColumnDef<any>[]>([]);
  useEffect(() => {
    setColumns(fromFormStructureToColDefs(structure, t, form));
    // Set the default values items aswell
    form.setValue("items", data);
  }, [structure, data, append]);

  function handleExport() {
    exportFormCsv(form.getValues().items, structure);
  }

  function handleImport(file: File) {
    importFormCsv(file, structure, (rows) => {
      form.reset({ items: rows });
    });
  }

  function handleDelete() {
    if (!table) {
      console.warn("Table is not ready yet");
      return;
    }

    const selectedRows = table?.getSelectedRowModel().flatRows || [];
    const selectedIndices = selectedRows.map((r) => r.index);
    const newItems = form
      .getValues()
      .items.filter((_, index) => !selectedIndices.includes(index));
    form.reset({ items: newItems });
    table?.resetRowSelection(true);
  }

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit(onSubmit)();
        }}
        className="space-y-6"
      >
        <DataTable
          data={fields}
          columns={columns}
          searchbar={!hideSearchbar}
          selectable
          tableReady={setTable}
          actions={
            disableRowAlteration ? (
              <></>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => append(formFromStructure(structure) as T)}
                >
                  {t("common.addRow")}
                </Button>

                <input
                  type="file"
                  accept=".csv"
                  className="hidden"
                  id="csv-upload"
                  onChange={(e) => {
                    if (e.target.files?.[0]) handleImport(e.target.files[0]);
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  className="m-2"
                  onClick={() => document.getElementById("csv-upload")?.click()}
                >
                  {t("common.import")}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="mr-2"
                  onClick={handleExport}
                >
                  {t("common.export")}
                </Button>
              </>
            )
          }
          selectionActions={
            <Button type="button" variant="destructive" onClick={handleDelete}>
              {t("common.deleteSelected")}
            </Button>
          }
        />

        <Button type="submit" disabled={form.formState.isSubmitting}>
          {form.formState.isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {t(overrideSaveButtonLable ?? "common.save")}
        </Button>
      </form>
    </Form>
  );
}

export function fromFormStructureToColDefs<T>(
  structure: FormStructure<T>,
  t: (key: string) => string,
  form: UseFormReturn<{ items: T[] }, any, { items: T[] }>,
): ColumnDef<T>[] {
  const colDefs: ColumnDef<T>[] = [];
  colDefs.push(defaultTableColumns["select"] as ColumnDef<T>);

  for (const [key, def] of Object.entries(structure) as [keyof T, any][]) {
    if (key === "virtualFields") continue;
    if (def.hidden) continue;

    colDefs.push({
      id: key as string,
      accessorKey: key as string,
      header: () => renderHeader(t(def.label), structure, key, form),
      cell: ({ row }) => renderBareField(t, structure, key, form, row.index),
    });
  }

  return colDefs;
}

export function renderBareField<T>(
  t: (key: string) => string,
  structure: FormStructure<T>,
  key: keyof T,
  form: UseFormReturn<{ items: T[] }, any, { items: T[] }>,
  rowIndex: number,
): ReactNode {
  return (
    <BulkFieldCell
      t={t}
      structure={structure}
      fieldKey={key}
      form={form}
      rowIndex={rowIndex}
    />
  );
}

// Watches this row's `rerender`-flagged fields (e.g. an "advanced" toggle) so that
// hiddenFn/disabledFn re-evaluate for this cell when a sibling field in the same
// row changes, mirroring how the single-entity form re-renders on those fields.
function BulkFieldCell<T>({
  t,
  structure,
  fieldKey,
  form,
  rowIndex,
}: {
  t: (key: string) => string;
  structure: FormStructure<T>;
  fieldKey: keyof T;
  form: UseFormReturn<{ items: T[] }, any, { items: T[] }>;
  rowIndex: number;
}): ReactNode {
  const rerenderNames = getRerenderFieldNames(structure).map(
    (name) => `items.${rowIndex}.${name}`,
  );
  useWatch({ control: form.control, name: rerenderNames as any });

  return (
    <FormField
      control={form.control}
      name={`items.${rowIndex}.${fieldKey as string}` as any}
      render={({ field }) => (
        <>
          {
            getField(
              t,
              structure,
              fieldKey,
              field,
              form,
              rowIndex,
            ) as ReactElement
          }
          <FormMessage />
        </>
      )}
    />
  );
}

export function renderHeader<T>(
  label: string,
  structure: FormStructure<T>,
  key: keyof T,
  form: UseFormReturn<{ items: T[] }, any, { items: T[] }>,
): ReactNode {
  const s = structure[key];
  const disabled = s?.disabled ?? false;
  
  if (isHeaderHidden(s, form))
    return null;
  
  function setAll(value: any) {
    const items = (form.getValues("items") as any[]) || [];
    const newItems = items.map((item, index) => {
      if (
        s.disabledFn?.(form as UseFormReturn<any>, {
          name: `items.${index}.${String(key)}`,
          value: item[key],
        } as any)
      ) {
        return item;
      }
      return { ...item, [key]: value };
    });
    form.reset({ items: newItems });
  }

  if (s.type === FormFieldType.COMBOBOX) {
    // Create a combobox that allows to select one option and set it for each row,
    // skipping rows whose disabledFn rejects the new value
    return (
      <ComboBox
        options={s.options as FormOption<any>[]}
        onChange={setAll}
        placeholder={label}
        disabled={disabled}
      />
    );
  }
  if (s.type === FormFieldType.CHECKBOX || s.type === FormFieldType.SWITCH) {
    // Create a checkbox/switch that allows to toggle for each row,
    // skipping rows whose disabledFn rejects the new value
    return (
      <>
        {s.type === FormFieldType.CHECKBOX ? (
          <Checkbox
            className="mr-1"
            onCheckedChange={setAll}
            disabled={disabled}
          />
        ) : (
          <Switch
            className="mr-1"
            onCheckedChange={setAll}
            disabled={disabled}
          />
        )}
        <span>{label}</span>
      </>
    );
  }
  if (s.type === FormFieldType.CONTACT_SELECT)
    return (
      <>
        <span className="mr-1">{label}</span>
        <ContactCreateDialog />
      </>
    );

  return <span>{label}</span>;
}

function isHeaderHidden<T>(
    s: FormStructure<T>[keyof T],
    form: UseFormReturn<{ items: T[] }, any, { items: T[] }>,
): boolean {
  if (!s.hiddenFn) return false;

  const values = form.getValues();
  if (!isBulkFormData<T>(values)) return false;

  return values.items === undefined || values.items.every((_, i) =>
      s.hiddenFn!(form as UseFormReturn<any>, i),
  );
}
